import React from 'react';
import { Image as ImageComponent } from '@src/stories/elements/Image/Image';
import { Video as VideoComponent } from '@src/stories/elements/Video/Video';
import { Button } from '@src/stories/elements/Button/Button';

import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

interface PromotionTileProps {
  settings: any;
  promotion: any;
  columns?: {
    mobile: number;
    desktop: number;
  };
  rows?: {
    mobile: number;
    desktop: number;
  };
}

export const PromotionTile: React.FC<PromotionTileProps> = ({ settings, promotion, columns, rows }) => {
  const { isMobile } = useResponsiveBreakpoints();
  const hasLink = !!promotion.link;
  const hasLinkLabel = !!promotion.link_label;
  // Get max columns from grid settings, fallback to 4 if not provided
  const maxColumnsMobile = columns?.mobile || 4;
  const maxColumnsDesktop = columns?.desktop || 4;

  const colSpanMobile = Math.max(1, Math.min(maxColumnsMobile, promotion.col_span_mobile || 1));
  const colSpanDesktop = Math.max(1, Math.min(maxColumnsDesktop, promotion.col_span_desktop || 1));

  // Get max rows from grid settings, fallback to 2 if not provided
  const maxRowsMobile = rows?.mobile || 2;
  const maxRowsDesktop = rows?.desktop || 2;
  
  const rowSpanMobile = Math.max(1, Math.min(maxRowsMobile, promotion.row_span_mobile || 1));
  const rowSpanDesktop = Math.max(1, Math.min(maxRowsDesktop, promotion.row_span_desktop || 1));

  // Determine if this promotional tile should align with product media height
  // Use the setting if available, otherwise fall back to previous logic
  const shouldUseProductMediaHeight = settings?.align_with_product_media_height !== undefined
    ? settings.align_with_product_media_height
    : (isMobile 
        ? colSpanMobile < maxColumnsMobile  // Not full width on mobile
        : colSpanDesktop < maxColumnsDesktop); // Not full width on desktop

  // Get responsive alignment classes
  const contentAlignment = isMobile
    ? promotion.settings?.content_alignment_mobile || 'center'
    : promotion.settings?.content_alignment_desktop || 'center';

  const verticalPosition = isMobile
    ? promotion.settings?.content_vertical_position_mobile || 'end'
    : promotion.settings?.content_vertical_position_desktop || 'end';

  // Get responsive text alignment classes
  const getTextAlignmentClass = (alignment: string) => {
    switch (alignment) {
      case 'start':
        return 'items-start';
      case 'center':
        return 'items-center';
      case 'end':
        return 'items-end';
      default:
        return 'items-center';
    }
  };

  const getJustifyClass = (position: string) => {
    switch (position) {
      case 'start':
        return 'justify-start';
      case 'center':
        return 'justify-center';
      case 'end':
        return 'justify-end';
      default:
        return 'justify-end';
    }
  };

  const textAlignClass = getTextAlignmentClass(contentAlignment);
  const justifyClass = getJustifyClass(verticalPosition);

  const TileContent = (
    <div
      className={`promotion-tile collection-tile fade-in relative col-span-${colSpanMobile} lg:col-span-${colSpanDesktop} row-span-${rowSpanMobile} lg:row-span-${rowSpanDesktop} h-full `}
      style={{ color: promotion.settings?.text_colour || 'white' }}
    >
      {/* Overlay link if link exists but no label */}
      {hasLink && !hasLinkLabel && (
        <a
          href={promotion.link}
          className='promotion-tile__link-overlay'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 10,
            textDecoration: 'none',
            cursor: 'pointer'
          }}
          aria-label={promotion.title || 'Promotion tile'}
          tabIndex={0}
        />
      )}
      <div
        className='promotion-tile__image-container relative'
        style={{
          minHeight: shouldUseProductMediaHeight ? 'auto' : (
            isMobile
              ? promotion.settings?.media_min_height_mobile
                ? `${promotion.settings.media_min_height_mobile}px`
                : 'auto'
              : promotion.settings?.media_min_height_desktop
              ? `${promotion.settings.media_min_height_desktop}px`
              : 'auto'
          ),
          height: shouldUseProductMediaHeight ? 'var(--product-media-height, auto)' : '100%'
        }}
      >
        <div className='promotion-tile__image-overlay h-full'>
          {promotion.video ? (
            <VideoComponent
              videoUrl={promotion.video}
              priority={false}
              autoplay={true}
              muted={true}
              loop={true}
              playsInline={true}
              sizingMode={'responsive'}
              classes='promotion-tile__video w-full h-full [&_video]:h-full  [&_video]:w-full  [&_video]:object-cover'
            />
          ) : (
            <ImageComponent
              imageUrl={promotion.image}
              isPriority={false}
              classes='object-fit promotion-tile__image h-full w-full'
              width={promotion.image.width}
              height={promotion.image.height}
            />
          )}
          <div
            className={`promotion-tile__content absolute inset-0 flex flex-col ${textAlignClass} ${justifyClass}`}
            style={{
              padding: isMobile
                ? `${promotion.settings?.padding_top_botton_mobile || 20}px ${promotion.settings?.padding_left_right_mobile || 12}px`
                : `${promotion.settings?.padding_top_bottom_desktop || 12}px ${promotion.settings?.padding_left_right_desktop || 20}px`,
              gap: isMobile
                ? `${promotion.settings?.content_gap_mobile || 0}px`
                : `${promotion.settings?.content_gap_desktop || 0}px`,
              color: promotion.settings?.text_colour || 'white',
              backgroundColor: promotion.background_color || 'var(--background-color)'
            }}
          >
            {promotion.title && (
              <div
                className='promotion-tile__title'
                style={{
                  marginBottom: isMobile
                    ? `${promotion.settings?.title_margin_bottom_mobile}px`
                    : `${promotion.settings?.title_margin_bottom_desktop}px`,
                  maxWidth: isMobile
                    ? promotion.settings?.title_max_width_mobile
                      ? `${promotion.settings.title_max_width_mobile}px`
                      : 'none'
                    : promotion.settings?.title_max_width_desktop
                    ? `${promotion.settings.title_max_width_desktop}px`
                    : 'none'
                }}
              >
                <h3
                  className={
                    isMobile
                      ? promotion.settings?.title_text_style_mobile || 'b1'
                      : promotion.settings?.title_text_style_desktop || 'b1'
                  }
                >
                  {promotion.title}
                </h3>
              </div>
            )}
            {promotion.subtitle && (
              <div
                className='promotion-tile__subtitle'
                style={{
                  marginBottom: isMobile
                    ? `${promotion.settings?.subtitle_margin_bottom_mobile}px`
                    : `${promotion.settings?.subtitle_margin_bottom_desktop}px`,
                  maxWidth: isMobile
                    ? promotion.settings?.subtitle_max_width_mobile
                      ? `${promotion.settings.subtitle_max_width_mobile}px`
                      : 'none'
                    : promotion.settings?.subtitle_max_width_desktop
                    ? `${promotion.settings.subtitle_max_width_desktop}px`
                    : 'none'
                }}
              >
                <h4
                  className={
                    isMobile
                      ? promotion.settings?.subtitle_text_style_mobile || 'b1'
                      : promotion.settings?.subtitle_text_style_desktop || 'b1'
                  }
                >
                  {promotion.subtitle}
                </h4>
              </div>
            )}
            {promotion.description && (
              <div
                className='promotion-tile__description'
                style={{
                  marginBottom: isMobile
                    ? `${promotion.settings?.description_margin_bottom_mobile}px`
                    : `${promotion.settings?.description_margin_bottom_desktop}px`,
                  maxWidth: isMobile
                    ? promotion.settings?.description_max_width_mobile
                      ? `${promotion.settings.description_max_width_mobile}px`
                      : 'none'
                    : promotion.settings?.description_max_width_desktop
                    ? `${promotion.settings.description_max_width_desktop}px`
                    : 'none'
                }}
              >
                <p
                  className={
                    isMobile
                      ? promotion.settings?.description_text_style_mobile || 'b1'
                      : promotion.settings?.description_text_style_desktop || 'b1'
                  }
                >
                  {promotion.description}
                </p>
              </div>
            )}
            <div className='promotion-tile__link'>
              {hasLink && hasLinkLabel && (
                <Button
                  label={promotion.link_label}
                  style={promotion.settings?.button_style || 'primary'}
                  type={promotion.settings?.cta_style_type || 'button'}
                  size={
                    promotion.settings?.cta_style_type === 'link'
                      ? promotion.settings?.link_size || 'lg'
                      : promotion.settings?.button_size || 'lg'
                  }
                  icon={promotion.settings?.has_icon ? 'ur-arrow' : undefined}
                  link={promotion.link}
                  reverse={true}
                  onClick={undefined}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return TileContent;
};

export default PromotionTile;
